import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listUsers } from '@/shared/services/usersService';
import { listRestaurants, listPendingRestaurants } from '@/shared/services/restaurantsService';
import { ordersService } from '@/shared/services/ordersService';

import styles from './DashboardPage.module.css';

interface DashboardStats {
  totalUsers: number;
  totalRestaurants: number;
  activeRestaurants: number;
  totalCouriers: number;
  activeCouriers: number;
  pendingRestaurants: number;
  pendingCouriers: number;
  activeIncidents: number;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalRestaurants: 0,
    activeRestaurants: 0,
    totalCouriers: 0,
    activeCouriers: 0,
    pendingRestaurants: 0,
    pendingCouriers: 0,
    activeIncidents: 0,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      // Cargar datos en paralelo
      const [users, restaurants, pendingRestaurantsList, incidentsData] = await Promise.all([
        listUsers(),
        listRestaurants(),
        listPendingRestaurants(),
        ordersService.getIncidents().catch(() => ({ orders: [] })),
      ]);

      // Calcular estadísticas
      const totalUsers = users.length;
      const totalRestaurants = restaurants.length;
      const activeRestaurants = restaurants.filter((r) => r.status === 'activo').length;

      // Filtrar usuarios por rol
      const couriers = users.filter((u) => u.role === 'courier');
      const totalCouriers = couriers.length;
      const activeCouriers = couriers.filter((u) => u.status === 'active').length;

      // Pendientes
      const pendingRestaurants = pendingRestaurantsList.length;

      // TODO: Agregar endpoint para repartidores pendientes cuando esté disponible
      const pendingCouriers = 0; // Por ahora 0 hasta que tengamos el endpoint

      setStats({
        totalUsers,
        totalRestaurants,
        activeRestaurants,
        totalCouriers,
        activeCouriers,
        pendingRestaurants,
        pendingCouriers,
        activeIncidents: incidentsData.orders.length,
      });
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      setError('Error al cargar las estadísticas. Intenta recargar la página.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>Vista general del sistema</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Cards de Métricas */}
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className={styles.metricContent}>
            <h3 className={styles.metricValue}>{stats.totalUsers.toLocaleString()}</h3>
            <p className={styles.metricLabel}>Usuarios Totales</p>
          </div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div className={styles.metricContent}>
            <h3 className={styles.metricValue}>{stats.totalRestaurants.toLocaleString()}</h3>
            <p className={styles.metricLabel}>Restaurantes</p>
            <p className={styles.metricSubtext}>{stats.activeRestaurants} activos</p>
          </div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <div className={styles.metricContent}>
            <h3 className={styles.metricValue}>{stats.totalCouriers.toLocaleString()}</h3>
            <p className={styles.metricLabel}>Repartidores</p>
            <p className={styles.metricSubtext}>{stats.activeCouriers} activos</p>
          </div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className={styles.metricContent}>
            <h3 className={styles.metricValue}>
              {(stats.pendingRestaurants + stats.pendingCouriers).toLocaleString()}
            </h3>
            <p className={styles.metricLabel}>Pendientes</p>
            <p className={styles.metricSubtext}>Requieren atención</p>
          </div>
        </div>
      </div>

      {/* Sección Principal */}
      <div className={styles.mainSection}>
        {/* Acciones Rápidas */}
        <div className={styles.quickActions}>
          <h2 className={styles.sectionTitle}>⚡ Acciones Rápidas</h2>
          <div className={styles.actionsGrid}>
            <button
              className={styles.actionButton}
              onClick={() => navigate('/restaurants/pending')}
            >
              <div className={styles.actionIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className={styles.actionContent}>
                <h3>Solicitudes Pendientes</h3>
                <p>Revisar y aprobar restaurantes</p>
              </div>
              {stats.pendingRestaurants > 0 && (
                <span className={styles.actionBadge}>{stats.pendingRestaurants}</span>
              )}
            </button>

            <button
              className={styles.actionButton}
              onClick={() => navigate('/restaurants')}
            >
              <div className={styles.actionIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className={styles.actionContent}>
                <h3>Restaurantes</h3>
                <p>Gestionar restaurantes activos</p>
              </div>
            </button>

            <button
              className={styles.actionButton}
              onClick={() => navigate('/restaurants/rejected')}
            >
              <div className={styles.actionIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="9" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <div className={styles.actionContent}>
                <h3>Solicitudes Rechazadas</h3>
                <p>Ver historial de rechazos</p>
              </div>
            </button>

            <button
              className={styles.actionButton}
              onClick={() => navigate('/users')}
            >
              <div className={styles.actionIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className={styles.actionContent}>
                <h3>Usuarios</h3>
                <p>Gestionar usuarios del sistema</p>
              </div>
            </button>
          </div>
        </div>

        {/* Pendientes que Requieren Atención */}
        {(stats.pendingRestaurants > 0 || stats.pendingCouriers > 0) && (
          <div className={styles.pendingSection}>
            <h2 className={styles.sectionTitle}>⚠️ Requiere Atención</h2>
            <div className={styles.pendingList}>
              {stats.pendingRestaurants > 0 && (
                <div className={styles.pendingItem}>
                  <div className={styles.pendingIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </div>
                  <div className={styles.pendingContent}>
                    <h3>{stats.pendingRestaurants} restaurante{stats.pendingRestaurants !== 1 ? 's' : ''} pendiente{stats.pendingRestaurants !== 1 ? 's' : ''}</h3>
                    <p>Esperando aprobación</p>
                  </div>
                  <button
                    className={styles.pendingButton}
                    onClick={() => navigate('/restaurants/pending')}
                  >
                    Revisar
                  </button>
                </div>
              )}

              {stats.pendingCouriers > 0 && (
                <div className={styles.pendingItem}>
                  <div className={styles.pendingIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className={styles.pendingContent}>
                    <h3>{stats.pendingCouriers} repartidor{stats.pendingCouriers !== 1 ? 'es' : ''} pendiente{stats.pendingCouriers !== 1 ? 's' : ''}</h3>
                    <p>Esperando aprobación</p>
                  </div>
                  <button
                    className={styles.pendingButton}
                    onClick={() => navigate('/couriers/pending')}
                  >
                    Revisar
                  </button>
                </div>
              )}

              {stats.activeIncidents > 0 && (
                <div className={styles.pendingItem}>
                  <div className={styles.pendingIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <div className={styles.pendingContent}>
                    <h3>🚨 {stats.activeIncidents} pedido{stats.activeIncidents !== 1 ? 's' : ''} con incidencia{stats.activeIncidents !== 1 ? 's' : ''}</h3>
                    <p>Requieren atención inmediata</p>
                  </div>
                  <button
                    className={styles.pendingButton}
                    onClick={() => navigate('/incidents')}
                  >
                    Ver Ahora
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Botón de actualizar */}
      <div className={styles.footer}>
        <button onClick={loadStats} className={styles.refreshButton} disabled={loading}>
          {loading ? 'Actualizando...' : '🔄 Actualizar Estadísticas'}
        </button>
      </div>
    </div>
  );
}
